import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { URLS } from "../constants/index";
import { motion, AnimatePresence } from "framer-motion";
import api from "../utils/api";

interface RecoverPasswordFormInputs {
  email: string;
}

interface RecoverPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email("login.emailInvalid")
    .required("login.emailRequired"),
});

const defaultValues: RecoverPasswordFormInputs = { email: "" };

const RecoverPasswordModal: React.FC<RecoverPasswordModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [recoverPasswordError, setRecoverPasswordError] = useState<
    string | null
  >(null);
  const [recoverPasswordSuccess, setRecoverPasswordSuccess] = useState<
    string | null
  >(null);

  const { handleSubmit, register, formState } =
    useForm<RecoverPasswordFormInputs>({
      defaultValues,
      resolver: yupResolver(validationSchema),
    });
  const { errors } = formState;

  const onSubmitForm: SubmitHandler<
    RecoverPasswordFormInputs
  > = async values => {
    setLoading(true);
    setRecoverPasswordError(null);
    try {
      const response = await api.post(
        `${URLS.CLIENT_URL}/api/recover-password`,
        { email: values.email },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.status === 200) {
        setRecoverPasswordSuccess(
          t("A password reset link has been sent to your email."),
        );
        setTimeout(onClose, 3000);
      } else {
        const err = response.data;
        setRecoverPasswordError(
          err.message || t("An error occurred while sending the reset link."),
        );
      }
    } catch {
      setRecoverPasswordError(
        t("An error occurred while sending the reset link."),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <Dialog
            open
            fullScreen={fullScreen}
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            BackdropProps={{ style: { backdropFilter: "blur(5px)" } }}
          >
            <DialogTitle>
              {t("Recover Password") || "Recover Password"} {/* Fallback */}
              <IconButton
                aria-label={t("common.close") || "Close"}
                onClick={onClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "#4C3699",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                p: 2,
                ...(fullScreen && {
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }),
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmitForm)}
                sx={{ width: "100%" }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <HelpOutlineIcon sx={{ color: "#4C3699", mr: 1 }} />
                  <Typography variant="body2">
                    {t("reset_your_password.") ||
                      "To reset your password, please enter your email below and click reset."}{" "}
                    {/* Fallback */}
                  </Typography>
                </Box>

                <AnimatePresence>
                  {recoverPasswordError && (
                    <motion.div
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {recoverPasswordError}
                      </Alert>
                    </motion.div>
                  )}
                  {recoverPasswordSuccess && (
                    <motion.div
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Alert severity="success" sx={{ mb: 2 }}>
                        {recoverPasswordSuccess}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                <TextField
                  label={t("Email") || "Email"} // Fallback caso a tradução falhe
                  type="email"
                  fullWidth
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message || t("login.emailRequired")} // Display proper error message with fallback
                  variant="filled"
                  size="small"
                  InputProps={{
                    disableUnderline: true,
                    sx: { borderRadius: 1, backgroundColor: "#4140421A" },
                  }}
                  autoFocus
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <motion.div
                    animate={loading ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                    transition={
                      loading
                        ? { duration: 0.8, repeat: Infinity }
                        : { duration: 0.2 }
                    }
                  >
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#4C3699",
                        borderRadius: 3,
                        py: { xs: 1, md: 1.25 },
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: "bold",
                        "&:hover": { backgroundColor: "#4C3699" },
                        mb: 3,
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        t("Reset Password") || "Reset Password" // Fallback caso a tradução falhe
                      )}
                    </Button>
                  </motion.div>
                </Box>
              </Box>
            </DialogContent>
          </Dialog>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecoverPasswordModal;
